import { byCss } from '@oracle/oraclejet-testing/UNSAFE_Locators';
import { TestAdapter } from '@oracle/oraclejet-testing/UNSAFE_TestAdapter';
import { TestElement } from '@oracle/oraclejet-testing/UNSAFE_Driver';

export class MessageBannerAdapter extends TestAdapter {
  /**
   * Closes the message identified by the provided key.
   * @param messageLocator.key {string | number} The key within the MessageBanner's dataset associated with the individual message.
   */
  async doClose(messageLocator: { key: string | number }) {
    const driver = this.getTestDriver();
    const messageSelector = this.getMessageSelector(messageLocator.key);
    const closeButtonSelector = '[data-oj-message-close-button] button';

    let closeButton;
    try {
      closeButton = await (
        await this.getElement()
      ).waitForElement(byCss(`${messageSelector} ${closeButtonSelector}`));
    } catch {
      throw new Error('The close operation is not applicable for the messageLocator.');
    }

    await driver.click(closeButton);
  }

  /**
   * Retrieve a TestElement which represents the custom detail content of a single message.
   * @param messageLocator.key {string | number} The key within the MessageBanner's dataset associated with the individual message.
   * @throws {Error} when the detail content rendered is not provided by the application
   * @returns The detail content of the message rendered by the application
   */
  async findDetail(messageLocator: { key: string | number }): Promise<TestElement> {
    const key = messageLocator.key;
    const messageSelector = this.getMessageSelector(key);
    const customDetailContentSelector = '[data-oj-message-custom-detail]';

    try {
      const detail = await (
        await this.getElement()
      ).waitForElement(byCss(`${messageSelector} ${customDetailContentSelector}`));
      return detail;
    } catch {
      throw new Error('No corresponding detail template or renderer found for the messageLocator.');
    }
  }

  /**
   * Creates the CSS selector for the message with the provided key.
   *
   * @param key The key of the message
   * @returns the css selector that can used to fetch the message
   */
  private getMessageSelector(key: string | number) {
    return typeof key === 'number'
      ? `[data-oj-key="number-${key}"]`
      : `[data-oj-key="string-${key}"]`;
  }
}
